import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, Typography, Box, CircularProgress, Button } from '@mui/material';
import { IconButton } from '@components/iconButton';
import { Times } from '@assets/svgs';

export interface DocumentDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  downloadUrl?: string | null;
  fileName?: string;
  fileType?: string | null;
  children?: ReactNode; // optional custom content instead of iframe
  height?: string | number; // default 90vh
  maxWidth?: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  /**
   * Called if the fetch of downloadUrl fails.
   */
  onError?: (err: unknown) => void;
  /** Show a fallback download link if inline PDF rendering is blocked (e.g. iOS / content-disposition attachment). */
  showDownloadFallback?: boolean;
}

export const DocumentDialog: FC<DocumentDialogProps> = ({
  open,
  onClose,
  title = 'Document Viewer',
  downloadUrl,
  fileName = 'document',
  fileType,
  children,
  height = '90vh',
  maxWidth = 'xl',
  fullWidth = true,
  onError,
  showDownloadFallback = true,
}) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [inlineFailed, setInlineFailed] = useState(false);
  const [previewMode, setPreviewMode] = useState<'image' | 'document' | 'download'>('document');

  const inferContentType = useCallback(
    async (blob: Blob): Promise<string> => {
      const lowerFileName = fileName?.toLowerCase() ?? '';
      const lowerDownloadUrl = downloadUrl?.toLowerCase() ?? '';
      const lowerFileType = fileType?.toLowerCase() ?? '';
      const nameOrUrl = `${lowerFileName} ${lowerDownloadUrl}`;

      if (lowerFileType && lowerFileType !== 'application/octet-stream') return lowerFileType;
      if (nameOrUrl.includes('.pdf')) return 'application/pdf';
      if (nameOrUrl.match(/\.(png)(?:$|[?#\s])/)) return 'image/png';
      if (nameOrUrl.match(/\.(jpe?g)(?:$|[?#\s])/)) return 'image/jpeg';
      if (nameOrUrl.match(/\.(gif)(?:$|[?#\s])/)) return 'image/gif';
      if (nameOrUrl.match(/\.(svg)(?:$|[?#\s])/)) return 'image/svg+xml';
      if (nameOrUrl.match(/\.(webp)(?:$|[?#\s])/)) return 'image/webp';
      if (nameOrUrl.match(/\.(avif)(?:$|[?#\s])/)) return 'image/avif';
      if (blob.type && blob.type !== 'application/octet-stream') return blob.type;

      const header = new Uint8Array(await blob.slice(0, 32).arrayBuffer());
      const asciiHeader = new TextDecoder('latin1').decode(header).toLowerCase();
      if (header[0] === 0x25 && header[1] === 0x50 && header[2] === 0x44 && header[3] === 0x46)
        return 'application/pdf';
      if (
        header[0] === 0x89 &&
        header[1] === 0x50 &&
        header[2] === 0x4e &&
        header[3] === 0x47 &&
        header[4] === 0x0d &&
        header[5] === 0x0a &&
        header[6] === 0x1a &&
        header[7] === 0x0a
      ) {
        return 'image/png';
      }
      if (header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff) return 'image/jpeg';
      if (asciiHeader.startsWith('gif87a') || asciiHeader.startsWith('gif89a')) return 'image/gif';
      if (asciiHeader.startsWith('riff') && asciiHeader.includes('webp')) return 'image/webp';
      if (asciiHeader.includes('ftypavif') || asciiHeader.includes('ftypavis')) return 'image/avif';
      if (asciiHeader.trimStart().startsWith('<svg')) return 'image/svg+xml';

      return 'application/octet-stream';
    },
    [downloadUrl, fileName, fileType],
  );

  // Fetch logic: only when dialog opens, no direct src, and downloadUrl present.
  useEffect(() => {
    let cancelled = false;
    const shouldFetch = open && !!downloadUrl;
    if (!shouldFetch) return;
    setLoading(true);
    setError(null);
    setInlineFailed(false);

    (async () => {
      try {
        // Try to fetch with headers that encourage inline display
        const response = await fetch(downloadUrl!, {
          headers: {
            Accept: 'application/pdf,image/*,*/*',
            'Cache-Control': 'no-cache',
            // Add user agent to help with server-side decisions
            'X-Requested-With': 'XMLHttpRequest',
          },
          mode: 'cors',
          // Prevent browser from automatically handling the response
          redirect: 'follow',
        });

        if (!response.ok) throw new Error(`Failed to fetch document: ${response.status}`);

        const contentDisposition = response.headers.get('content-disposition');
        if (contentDisposition && contentDisposition.includes('attachment')) {
          console.warn('⚠️ Server is forcing download with Content-Disposition: attachment');
        }

        const blob = await response.blob();

        const contentType = await inferContentType(blob);
        // Create a new blob with the correct MIME type and remove any problematic headers
        const typedBlob = new Blob([blob], {
          type: contentType,
        });

        setPreviewMode(
          typedBlob.type.startsWith('image/')
            ? 'image'
            : typedBlob.type === 'application/pdf'
              ? 'document'
              : 'download',
        );

        const url = URL.createObjectURL(typedBlob);

        if (!cancelled) {
          setObjectUrl(url);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
          onError?.(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, downloadUrl, fileName, fileType, inferContentType, onError]);

  // Revoke object URL when closing / unmounting or when a new one replaces it.
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  // When dialog closes, flush object URL for security/memory and reset state.
  useEffect(() => {
    if (!open) {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        setObjectUrl(null);
      }
      // Reset all state when dialog closes
      setInlineFailed(false);
      setError(null);
      setPreviewMode('document');
    }
  }, [open, objectUrl]);

  const effectiveSrc = objectUrl || null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth={fullWidth}>
      <DialogTitle sx={{ p: 0, m: 0, display: 'flex', alignItems: 'center' }}>
        <Typography variant='h6' fontWeight={600} sx={{ p: 0, flex: 1 }}>
          {title}
        </Typography>
        <IconButton aria-label='close' onClick={onClose}>
          <Times />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ m: 0, p: 0, height, overflow: 'hidden' }}>
        {children ? (
          children
        ) : loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <CircularProgress size={32} />
            <Typography variant='body2' sx={{ ml: 2 }}>
              Loading document...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Typography variant='body2' color='error.main'>
              Failed to load document: {error instanceof Error ? error.message : 'Unknown error'}
            </Typography>
          </Box>
        ) : effectiveSrc ? (
          previewMode === 'image' ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                position: 'relative',
              }}
            >
              <img
                src={effectiveSrc}
                alt={title}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
                onError={() => {
                  setInlineFailed(true);
                }}
              />
            </Box>
          ) : previewMode === 'download' ? (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant='body2'>Inline preview is unavailable for this file type.</Typography>
              <Button
                variant='contained'
                onClick={() => {
                  const a = document.createElement('a');
                  a.href = effectiveSrc;
                  a.download = fileName;
                  a.click();
                }}
                sx={{ width: 'fit-content' }}
              >
                Download {fileName}
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                position: 'relative',
              }}
            >
              <iframe
                src={effectiveSrc}
                title={title}
                width='100%'
                height='100%'
                style={{
                  padding: 0,
                  margin: 0,
                  display: 'block',
                  marginTop: '30px',
                }}
              />
            </Box>
          )
        ) : inlineFailed && showDownloadFallback && effectiveSrc ? (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant='body2'>Inline preview unavailable. You can download the document instead.</Typography>
            <Button
              variant='contained'
              onClick={() => {
                const a = document.createElement('a');
                a.href = effectiveSrc;
                a.download = fileName;
                a.click();
              }}
              sx={{ width: 'fit-content' }}
            >
              Download {fileName}
            </Button>
          </Box>
        ) : effectiveSrc ? (
          // Last resort: try to display the content anyway
          <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
            <Typography variant='body2' sx={{ p: 2, backgroundColor: 'warning.light' }}>
              Display issue detected. Attempting fallback render...
            </Typography>
            <iframe
              src={effectiveSrc}
              title={title}
              width='100%'
              height='calc(100% - 50px)'
              style={{ border: 0, marginTop: '50px' }}
            />
          </Box>
        ) : !downloadUrl ? (
          <Box sx={{ p: 2 }}>
            <Typography variant='body2' color='text.secondary'>
              No document URL provided.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            <Typography variant='body2' color='text.secondary'>
              Unable to display document. Please try downloading it instead.
            </Typography>
            {downloadUrl && (
              <Button
                variant='outlined'
                onClick={() => {
                  window.open(downloadUrl, '_blank');
                }}
                sx={{ mt: 2, width: 'fit-content' }}
              >
                Open in New Tab
              </Button>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
